import React, { useEffect, useState } from 'react';
import { Alert, Button, Drawer, Spin } from 'antd';
import { Link } from 'react-router-dom';
import { catalogueRequest } from './api';
import { CatalogueAsset, CatalogueIndex, CatalogueProfile } from './types';
import PricePreview from './PricePreview';

export default function AssetDetails({
  asset,
  index,
  admin,
  sessionId,
  onClose,
}: {
  asset: CatalogueAsset;
  index: CatalogueIndex;
  admin: boolean;
  sessionId?: number;
  onClose: () => void;
}) {
  const [profile, setProfile] = useState<CatalogueProfile | null>(null);
  const [error, setError] = useState('');
  const [showPrices, setShowPrices] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setProfile(null);
    setError('');
    setShowPrices(false);
    catalogueRequest<CatalogueProfile>(
      `assets/${asset.id}`,
      'GET',
      {
        scope: admin ? 'admin' : 'selection',
        ...(sessionId !== undefined ? { session_id: sessionId } : {}),
      },
      controller.signal,
    )
      .then(data => {
        if (!controller.signal.aborted) setProfile(data);
      })
      .catch(reason => {
        if (!controller.signal.aborted) setError(reason.message);
      });
    return () => controller.abort();
  }, [asset.id, admin, sessionId]);
  return (
    <Drawer
      open
      onClose={onClose}
      title={`${asset.ticker} · ${asset.name}`}
      size="large"
    >
      <div className="ac-details">
        <p className="ac-muted">
          PM class: {asset.resourcetype || asset.runtime_type} ·{' '}
          {asset.retired ? 'Retired' : asset.visible ? 'Visible' : 'Hidden'}
        </p>
        <p className="ac-muted">
          {asset.currency} · {asset.ric || asset.ticker}
          {asset.isin ? ` · ${asset.isin}` : ''}
          {asset.issue_date ? ` · Issued ${asset.issue_date}` : ''}
          {asset.maturity_date ? ` · Matures ${asset.maturity_date}` : ''}
        </p>
        {error && <Alert type="error" title={error} />}
        {!profile && !error && <Spin />}
        {profile && (
          <>
            {admin && (
              <Link to={`/dashboard/catalogue-studio?asset=${asset.id}`}>
                Edit in Catalogue Studio
              </Link>
            )}
            <>
              <strong>{profile.primary_role || 'Desk note'}</strong>
              <p className="ac-prose">
                {profile.desk_note || 'No desk note has been added yet.'}
              </p>
              {profile.implementation_note && (
                <>
                  <strong>Implementation</strong>
                  <p className="ac-prose">{profile.implementation_note}</p>
                </>
              )}
              {profile.memberships.map(m => (
                <div className="ac-membership" key={m.collection_id}>
                  <strong>
                    {
                      index.collections.find(c => c.id === m.collection_id)
                        ?.label
                    }{' '}
                    {m.role === 'reference' ? '· Reference' : ''}
                  </strong>
                  {m.rationale && <p className="ac-prose">{m.rationale}</p>}
                </div>
              ))}
              <small>{profile.source}</small>
            </>
          </>
        )}
        <Button onClick={() => setShowPrices(value => !value)}>
          {showPrices ? 'Hide stored quotes' : 'Preview stored quotes'}
        </Button>
        {showPrices && (
          <PricePreview
            assetId={asset.id}
            admin={admin}
            sessionId={sessionId}
          />
        )}
      </div>
    </Drawer>
  );
}
